import { useEffect, useState } from "react";
import {
  Building2,
  Calculator,
  Handshake,
  KeyRound,
  Landmark,
  Stethoscope,
  UserRound,
  Wallet,
  Zap,
  type LucideIcon,
} from "lucide-react";

import { useReducedMotion } from "@/hooks/useReducedMotion";
import { cn } from "@/shared/lib/cn";
import { BrandMark } from "@/shared/ui/brand/BrandMark";

import {
  boxStyle,
  CANVAS,
  HUB,
  NODES,
  nodeBox,
  TRANSACTIONS,
  transactionEnds,
  transactionPath,
  type EcoIcon,
  type EcoNode,
  type EcoTransaction,
} from "../lib/ecosystem";

const ICONS: Record<EcoIcon, LucideIcon> = {
  provider: Stethoscope,
  clearinghouse: Landmark,
  partner: KeyRound,
  adjudication: Calculator,
  employer: Building2,
  member: UserRound,
  wallet: Wallet,
  rail: Zap,
  agent: Handshake,
};

/** How long each transaction holds the stage. */
const SCENE_MS = 3000;

/**
 * The transactions PayGate sits in the middle of, one at a time.
 *
 * Every party is on screen, greyed back, and a transaction lights the two it
 * moves money between — a claim from a provider adjudicated with the partner
 * TPA, premium from an employer paying the commissions it funds. The line runs
 * source → hub → destination as one path, so the pulse travelling it shows the
 * money passing through us rather than merely arriving somewhere.
 *
 * Drawn rather than filmed: it respects the theme, stays sharp at any size and
 * ships no megabytes. Cards are HTML on a percentage grid, only the connector
 * is SVG, and type scales in `cqw` off the container so labels hold their
 * proportions in the column this sits in. Decorative throughout, so it is
 * hidden from assistive tech — `BrandHero` carries the words.
 */
export function EcosystemIllustration({ className }: { className?: string }) {
  const active = TRANSACTIONS[useSceneCycle(TRANSACTIONS.length)]!;

  return (
    <div
      aria-hidden="true"
      className={cn("relative", className)}
      style={{
        aspectRatio: `${CANVAS.width} / ${CANVAS.height}`,
        containerType: "inline-size",
      }}
    >
      <Connector transaction={active} />

      {NODES.map((node) => (
        <NodeCard
          key={node.id}
          node={node}
          active={node.id === active.from || node.id === active.to}
        />
      ))}

      <Hub label={active.label} />
    </div>
  );
}

/**
 * Advances through the transactions, or holds on the first when the visitor has
 * asked for less motion — a diagram that never settles is unusable to anyone it
 * makes dizzy, and the still frame still shows every party.
 */
function useSceneCycle(count: number): number {
  const reduced = useReducedMotion();
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (reduced) {
      setIndex(0);
      return;
    }
    const timer = window.setInterval(() => setIndex((i) => (i + 1) % count), SCENE_MS);
    return () => window.clearInterval(timer);
  }, [reduced, count]);

  return index;
}

// ─── Connector ──────────────────────────────────────────────────────────────

function Connector({ transaction }: { transaction: EcoTransaction }) {
  const reduced = useReducedMotion();
  const path = transactionPath(transaction);
  const ends = transactionEnds(transaction);

  return (
    <svg
      className="absolute inset-0 h-full w-full"
      viewBox={`0 0 ${CANVAS.width} ${CANVAS.height}`}
      xmlns="http://www.w3.org/2000/svg"
    >
      <defs>
        <pattern id="ecoGrid" width={50} height={50} patternUnits="userSpaceOnUse">
          <path d="M 50 0 L 0 0 0 50" fill="none" stroke="currentColor" strokeWidth={1} />
        </pattern>
        {/* Without this the grid ends on a hard rectangle, which reads as a
            border around the illustration rather than as a backdrop. */}
        <radialGradient id="ecoGridFade">
          <stop offset="35%" stopColor="white" />
          <stop offset="100%" stopColor="black" />
        </radialGradient>
        <mask id="ecoGridMask">
          <rect width={CANVAS.width} height={CANVAS.height} fill="url(#ecoGridFade)" />
        </mask>
        {/* Laid along the run itself, so the colour shift tracks the money. */}
        <linearGradient
          id="ecoFlow"
          gradientUnits="userSpaceOnUse"
          x1={ends.from.x}
          y1={ends.from.y}
          x2={ends.to.x}
          y2={ends.to.y}
        >
          <stop offset="0%" stopColor="var(--brand-cyan-500)" />
          <stop offset="100%" stopColor="var(--primary)" />
        </linearGradient>
        <filter id="ecoGlow" x="-400%" y="-400%" width="900%" height="900%">
          <feGaussianBlur stdDeviation="5" result="blur" />
          <feMerge>
            <feMergeNode in="blur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>

      <rect
        width={CANVAS.width}
        height={CANVAS.height}
        fill="url(#ecoGrid)"
        mask="url(#ecoGridMask)"
        className="text-border/50"
      />

      {/* Only the transaction on stage is drawn. Every possible route at once
          is a hairball; one at a time is a story. */}
      <path
        d={path}
        fill="none"
        stroke="url(#ecoFlow)"
        strokeWidth={3}
        strokeLinecap="round"
        strokeLinejoin="round"
      />

      {!reduced && (
        // Keyed on the transaction so the motion restarts with the stage.
        <rect
          key={transaction.id}
          x={-7}
          y={-7}
          width={14}
          height={14}
          rx={5}
          fill="url(#ecoFlow)"
          filter="url(#ecoGlow)"
        >
          <animateMotion dur="2.2s" repeatCount="indefinite" path={path} />
        </rect>
      )}
    </svg>
  );
}

// ─── Cards ──────────────────────────────────────────────────────────────────

function NodeCard({ node, active }: { node: EcoNode; active: boolean }) {
  const Icon = ICONS[node.icon];

  return (
    <div
      style={boxStyle(nodeBox(node))}
      className={cn(
        "absolute flex flex-col items-center justify-center gap-[1.4cqw] rounded-[2cqw] border px-[1.5cqw] text-center backdrop-blur-sm transition-all duration-500",
        active
          ? "border-primary/60 bg-card shadow-lg shadow-primary/20"
          : "border-border/60 bg-card/50"
      )}
    >
      <Icon
        className={cn(
          "size-[2.6cqw] transition-colors duration-500",
          active ? "text-primary" : "text-muted-foreground/50"
        )}
      />
      <span
        className={cn(
          "text-[1.9cqw] font-medium leading-tight tracking-tight transition-colors duration-500",
          active ? "text-foreground" : "text-muted-foreground/80"
        )}
      >
        {node.label}
      </span>
    </div>
  );
}

/** The hub: our mark, and the transaction currently passing through it. */
function Hub({ label }: { label: string }) {
  return (
    <div
      style={boxStyle(HUB)}
      className="absolute flex flex-col items-center justify-center gap-[1.6cqw] rounded-[2.5cqw] border border-primary/20 bg-card px-[2cqw] text-center shadow-2xl shadow-primary/25"
    >
      <BrandMark label="" className="size-[4.6cqw]" />
      <span className="rounded-full border border-primary/25 bg-primary/10 px-[1.8cqw] py-[0.6cqw] text-[1.9cqw] font-semibold leading-tight text-primary">
        {label}
      </span>
    </div>
  );
}
